import ArrowUpIcon from "@/components/icons/ArrowUpIcon"
import rpc, { rpcTask } from "@/src/rpc"
import { sendMessage } from "@/src/useMessage"
import { useAppRouter } from "@/src/useAppRouter"
import classNames from "classnames"
import { marked } from "marked"
import { useEffect, useMemo, useRef, useState } from "react"


// Configure marked for safe, compact output
marked.setOptions({
  breaks: true,
  gfm: true,
})


export default function AssistantChat({visibleDate}) {

  const router = useAppRouter()
  const textareaRef = useRef(null)
  const messageListRef = useRef(null)
  const chatMessageSound = useRef(null)

  const [chatId, setChatId] = useState(null)
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [pendingMessage, setPendingMessage] = useState(null)
  const [showTyping, setShowTyping] = useState(false)
  const [suggestions, setSuggestions] = useState([])

  useEffect(() => {
    chatMessageSound.current = new Audio("/sounds/chat-message.mp3")
  }, [])


  useEffect(() => {
    loadRecentChat()
    focusInput()
  }, [])


  async function loadRecentChat() {
    try {
      const data = await rpc('assistant.getRecentChat')
      if (data?.chatId) {
        setChatId(data.chatId)
        setMessages(data.messages || [])
      }
    } catch (err) {
      // Ignore errors - just start a fresh chat
    }
  }


  useEffect(scrollMessages, [messages])


  const handleInputKeyDown = async (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (event.shiftKey) {
        const {selectionStart, selectionEnd} = event.target
        const newValue = inputValue.slice(0, selectionStart) + "\n" + inputValue.slice(selectionEnd)
        setInputValue(newValue)

        setTimeout(() => {
          resizeTextarea()
          if (selectionStart === inputValue.length) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight
          }
          textareaRef.current.selectionStart = selectionStart + 1
          textareaRef.current.selectionEnd = selectionStart + 1
        }, 0)
      } else {
        handleSend(inputValue)
      }
    }
  }


  function processUiActions(actions) {
    for (const action of actions) {
      switch (action.type) {
        case 'open_post':
          router.goto(action.date, {popover: 'edit', id: action.postGroupId})
          break
        case 'close_post':
          router.closePopover()
          break
        case 'navigate_to_month':
          router.goto(action.date)
          sendMessage('refreshCalendar')
          break
      }
    }
  }


  async function handleSend(text) {
    if (!text.trim()) return

    setSuggestions([])
    setPendingMessage({
      id: Date.now(),
      content: text,
      role: "user",
    })
    setInputValue("")
    setTimeout(() => {
      resizeTextarea()
      scrollMessages()
    }, 0)

    try {
      setShowTyping(true)

      const task = await rpcTask('assistant.sendMessage', {
        text,
        chatId,
        visibleDate,
      })

      // Wait for the task to complete and get the result
      const taskResult = await task.result()
      const newChatId = taskResult?.chatId || chatId
      if (newChatId) setChatId(newChatId)

      // Process UI actions from the assistant
      const uiActions = taskResult?.uiActions || []
      processUiActions(uiActions)

      // Fetch updated messages
      const data = await rpc('assistant.getMessages', {
        chatId: newChatId,
      })

      if (data) {
        setMessages(data.messages || [])
      }

      // Generate suggestions based on the tools used
      const toolsCalled = taskResult?.toolsCalled || []
      setSuggestions(getSuggestions(uiActions, toolsCalled))

      // Refresh the calendar in case the assistant made changes to posts
      sendMessage('refreshCalendar')

      if (chatMessageSound.current) {
        chatMessageSound.current.play().catch(() => {})
      }
    } catch (err) {
      console.error('Failed to send message:', err)
      // Add error message to chat
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: "Sorry, something went wrong. Please try again.",
      }])
    } finally {
      setShowTyping(false)
      setPendingMessage(null)
      focusInput()
    }
  }


  function getSuggestions(uiActions, toolsCalled) {
    // After deleting a post
    if (toolsCalled.includes('delete_post')) {
      return ['Undo']
    }

    // After creating a post
    if (toolsCalled.includes('create_post')) {
      return ['Show me the post', 'Create another']
    }

    // After moving/rescheduling
    if (toolsCalled.includes('reschedule_post')) {
      return ['Show me the post']
    }

    return []
  }


  function scrollMessages() {
    if (!messageListRef.current) return

    const lastMessage = messageListRef.current.lastChild
    if (lastMessage) {
      lastMessage.scrollIntoView({behavior: "smooth", block: "start"})
    } else {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight
    }
  }


  function focusInput() {
    if (textareaRef.current) textareaRef.current.focus()
  }


  function resizeTextarea() {
    if (!textareaRef.current) return
    textareaRef.current.style.height = `0px`
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
  }


  const messageList = messages.concat(pendingMessage ? [pendingMessage] : [])
  const canSend = inputValue.trim().length > 0 && !pendingMessage
  const canType = !pendingMessage

  let placeholder = "Ask me anything..."
  if (pendingMessage) {
    placeholder = "Sending..."
  }

  return <>
    <div className="assistant-chat flex-col full-height">

      <div ref={messageListRef} className="message-list flex-grow overflow-scroll-y flex-col gap-3 p-3">
        { messageList.length === 0 && !showTyping &&
          <div className="empty-state flex-col flex-justify-center gap-3 flex-align-center full-height gray text-center px-4 w-max" style={{maxWidth: '20em'}}>
            <h3 className="text-lg mb-1">{'\u{1F44B}'} Hello!</h3>
            <p className="text-sm">
              I am your AI assistant.
            </p>
            <p className="text-sm">
              You can ask me to create, delete, and reschedule posts.
            </p>
          </div>
        }

        {
          messageList.map((message, index) => {
            // Skip messages that only have tool call metadata
            if (message.metadata?.toolCalls && !message.content) return null

            const isUser = message.role === 'user'

            return (
              <div key={message.id || index} className={classNames("flex-row", {"flex-justify-end": isUser})}>
                <div className={classNames("chat-bubble fit-content py-2 px-3", {
                  "bg-blue white ml-6": isUser,
                  "bg-eee mr-6": !isUser,
                })}>
                  { isUser ?
                    <div className="pre-wrap forcewrap text-sm">
                      {message.content}
                    </div>
                  :
                    <MarkdownContent content={message.content}/>
                  }
                </div>
              </div>
            )
          })
        }

        { showTyping &&
          <div className="flex-row">
            <div className="chat-bubble fit-content py-2 px-3 bg-eee">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        }

        { !showTyping && suggestions.length > 0 &&
          <div className="flex-row flex-wrap gap-2 mt-1">
            { suggestions.map((suggestion) =>
              <button
                key={suggestion}
                className="suggestion-chip"
                onClick={() => handleSend(suggestion)}
              >
                {suggestion}
              </button>
            )}
          </div>
        }
      </div>

      <div className="input-area p-3 pt-0">
        <div className={classNames("flex-row flex-align-end border-gray shadow input-wrapper overflow-hidden", {'bg-white': canType, 'bg-f3f3f3': !canType})}>
          <textarea
            ref={textareaRef}
            className="message-input input no-border no-shadow pl-3 text-sm"
            value={inputValue}
            onChange={(e) => { setInputValue(e.target.value); resizeTextarea() }}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            disabled={!canType}
          />
          <button
            onClick={() => handleSend(inputValue)}
            className={classNames("button gray rounded-full send-button m-1 no-shadow no-border transition", {'is-primary': canSend, white: canSend, shadow: canSend})}
            disabled={!canSend}
          >
            <ArrowUpIcon bold/>
          </button>
        </div>
      </div>
    </div>

    <style jsx>{`
      .assistant-chat {
        background: white;
      }
      .message-list {
        min-height: 0;
      }
      .chat-bubble {
        border-radius: 1rem;
        max-width: 85%;
      }
      .input-wrapper {
        border-radius: 1.5rem;
      }
      .message-input {
        resize: none;
        max-height: 6rem;
      }
      .send-button {
        padding: 0.2rem !important;
        height: 1.5rem;
        width: 1.5rem;
      }
      .markdown-content :global(p) {
        margin: 0 0 0.4em 0;
      }
      .markdown-content :global(p:last-child) {
        margin-bottom: 0;
      }
      .markdown-content :global(ul),
      .markdown-content :global(ol) {
        margin: 0.25em 0;
        padding-left: 1.25em;
      }
      .markdown-content :global(li) {
        margin-bottom: 0.15em;
      }
      .markdown-content :global(strong) {
        font-weight: 600;
      }
      .markdown-content :global(code) {
        background: rgba(0, 0, 0, 0.06);
        padding: 0.1em 0.3em;
        border-radius: 3px;
        font-size: 0.85em;
      }
      .markdown-content :global(h1),
      .markdown-content :global(h2),
      .markdown-content :global(h3) {
        font-weight: 600;
        margin: 0.5em 0 0.25em 0;
        font-size: 1em;
      }
      .typing-indicator {
        display: flex;
        gap: 4px;
        align-items: center;
        padding: 4px 2px;
      }
      .typing-indicator span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: #999;
        animation: typing-bounce 1.4s ease-in-out infinite;
      }
      .typing-indicator span:nth-child(2) {
        animation-delay: 0.2s;
      }
      .typing-indicator span:nth-child(3) {
        animation-delay: 0.4s;
      }
      @keyframes typing-bounce {
        0%, 60%, 100% {
          transform: translateY(0);
          opacity: 0.4;
        }
        30% {
          transform: translateY(-4px);
          opacity: 1;
        }
      }
      .suggestion-chip {
        font-size: 0.8rem;
        padding: 0.3rem 0.75rem;
        border-radius: 1rem;
        border: 1px solid #ddd;
        background: white;
        color: #485fc7;
        cursor: pointer;
        transition: all 150ms;
      }
      .suggestion-chip:hover {
        background: #485fc7;
        color: white;
        border-color: #485fc7;
      }
    `}</style>

  </>
}


function MarkdownContent({content}) {
  const html = useMemo(() => {
    if (!content) return ''
    return marked.parse(content)
  }, [content])

  return (
    <div
      className="markdown-content text-sm"
      dangerouslySetInnerHTML={{__html: html}}
    />
  )
}
